import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { JDAnalysisPanel } from '../components/JDAnalysisPanel';
import { ResumeUploadZone } from '../components/ResumeUploadZone';
import { CandidateList } from '../components/CandidateList';
import { SmartCandidateTable } from '../components/SmartCandidateTable';
import MarketIntelligenceTab from '../components/MarketIntelligenceTab';
import DiscoveryQuestionsTab from '../components/DiscoveryQuestionsTab';
import SourcingStrategyTab from '../components/SourcingStrategyTab';
import { TopCandidates } from '../components/TopCandidates';
import { TalentHeatmap } from '../components/TalentHeatmap';
import { CandidateClusters } from '../components/CandidateClusters';
import { QuickCompareModal } from '../components/QuickCompare';
import { ResumeViewerModal } from '../components/ResumeViewerModal';
import { InterviewQuestionsEditor } from '../components/InterviewQuestionsEditor';
import { InterviewLinkModal } from '../components/InterviewLinkModal';
import { InterviewHistory } from '../components/InterviewHistory';
import { BatchProgressTracker } from '../components/BatchProgressTracker';
import {
  Brain, Users, Download, Loader2, RefreshCw, ChevronLeft,
  BarChart3, FileText, Sparkles, Trophy, MapPin, GitCompare,
  Layers, Video, Clock, Building2, Pencil, FileDown, Link2, Globe,
  Share2, Target, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getJob, getCandidates, exportCandidates, analyzeResumes,
  uploadResumes, getTopCandidates, getLocationHeatmap,
  getCandidateClusters, compareCandidates, getCandidateDetail,
  generateInterviewQuestions, getInterviewQuestions, updateInterviewQuestions,
  updateJob, exportPdfReport, getAllCandidates,
  createJDShareLink, createMIShareLink, createDiscoveryShareLink, createSourcingShareLink
} from '../services/api';
import { PublicLinkModal } from '../components/PublicLinkModal';
import PublicTestAnalytics from '../components/PublicTestAnalytics';
import CombinedShareModal from '../components/CombinedShareModal';
import ATSCandidateImportModal from '../components/ATSCandidateImportModal';
import { useAuth } from '../context/AuthContext';

const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || '').replace(/\/+$/, '') || (typeof window !== 'undefined' ? window.location.origin : '');

const ShareDropdown = ({ jobId, hasJDData, onOpenCombinedShare }) => {
  const [open, setOpen] = React.useState(false);
  const [sharing, setSharing] = React.useState(null);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleShareSingle = async (type) => {
    setSharing(type);
    try {
      const fn = type === 'jd' ? createJDShareLink : type === 'mi' ? createMIShareLink : type === 'discovery' ? createDiscoveryShareLink : createSourcingShareLink;
      const result = await fn(jobId);
      const url = `${PUBLIC_URL}/shared/${type}/${result.share_token}`;
      navigator.clipboard.writeText(url);
      toast.success(`${type.toUpperCase()} link copied!`);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `No ${type.toUpperCase()} analysis to share yet`);
    } finally {
      setSharing(null);
    }
  };

  const items = [
    { key: 'complete', label: 'Complete Share', icon: Sparkles, desc: 'Choose sections to share', color: 'text-primary' },
    { key: 'jd', label: 'JD Intelligence', icon: Target, desc: 'Share JD analysis', color: 'text-indigo-500' },
    { key: 'mi', label: 'MI Analysis', icon: Globe, desc: 'Share market intelligence', color: 'text-purple-500' },
    { key: 'discovery', label: 'Discovery Questions', icon: Brain, desc: 'Share discovery guide', color: 'text-teal-500' },
    { key: 'sourcing', label: 'Smart Sourcing', icon: Sparkles, desc: 'Share sourcing strategy', color: 'text-orange-500' },
  ];

  return (
    <div className="relative" ref={ref}>
      <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setOpen(!open)} data-testid="share-dropdown-btn">
        <Share2 className="h-3.5 w-3.5" /> Share
      </Button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-64 bg-background border rounded-xl shadow-xl z-50 py-1.5 animate-in fade-in-0 zoom-in-95" data-testid="share-dropdown-menu">
          {items.map(({ key, label, icon: Icon, desc, color }) => (
            <button key={key}
              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-muted/50 transition-colors text-left"
              onClick={() => {
                if (key === 'complete') { setOpen(false); onOpenCombinedShare(); }
                else handleShareSingle(key);
              }}
              disabled={sharing === key}
              data-testid={`share-option-${key}`}
            >
              <div className={`p-1.5 rounded-md bg-muted/50`}>
                {sharing === key ? <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" /> : <Icon className={`h-3.5 w-3.5 ${color}`} />}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium">{label}</p>
                <p className="text-[10px] text-muted-foreground">{desc}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function JobDetailPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const readOnly = user?.role === 'viewer';

  const [currentJob, setCurrentJob] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [topCandidates, setTopCandidates] = useState([]);
  const [heatmapData, setHeatmapData] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [activeTab, setActiveTab] = useState('candidates');

  const [showResumeModal, setShowResumeModal] = useState(false);
  const [selectedResumeCandidate, setSelectedResumeCandidate] = useState(null);

  const [interviewQuestions, setInterviewQuestions] = useState([]);
  const [generatingQuestions, setGeneratingQuestions] = useState(false);
  const [savingQuestions, setSavingQuestions] = useState(false);
  const [showInterviewLinkModal, setShowInterviewLinkModal] = useState(false);
  const [interviewLinkCandidate, setInterviewLinkCandidate] = useState(null);

  const [loadingJob, setLoadingJob] = useState(true);
  const [uploadingResumes, setUploadingResumes] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [analyzingResumes, setAnalyzingResumes] = useState(false);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loadingTopCandidates, setLoadingTopCandidates] = useState(false);
  const [loadingHeatmap, setLoadingHeatmap] = useState(false);
  const [loadingClusters, setLoadingClusters] = useState(false);
  const [loadingCompare, setLoadingCompare] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [showPublicLinkModal, setShowPublicLinkModal] = useState(false);
  const [showCombinedShareModal, setShowCombinedShareModal] = useState(false);
  const [showATSCandidateImport, setShowATSCandidateImport] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [fitFilter, setFitFilter] = useState('all');
  const [recommendationFilter, setRecommendationFilter] = useState('all');
  const [enrichedCandidates, setEnrichedCandidates] = useState([]);

  // Auto-imported Ceipal jobs get their AI analysis on first open — refresh until it lands
  useEffect(() => {
    if (currentJob?.jd_analysis_status !== 'queued' || currentJob?.jd_analysis) return undefined;
    const t = setTimeout(async () => { try { setCurrentJob(await getJob(jobId)); } catch { /* keep current view */ } }, 5000);
    return () => clearTimeout(t);
  }, [currentJob, jobId]);

  useEffect(() => {
    if (jobId) {
      fetchJobDetails();
      setTimeout(() => {
        fetchCandidates();
        fetchTopCandidates();
        fetchHeatmap();
        fetchClusters();
        fetchInterviewQuestions();
      }, 300);
    }
  }, [jobId]);

  const fetchJobDetails = async () => {
    try {
      setLoadingJob(true);
      const data = await getJob(jobId);
      setCurrentJob(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load job');
      navigate('/jobs');
    } finally {
      setLoadingJob(false);
    }
  };

  const fetchCandidates = async () => {
    try {
      setLoadingCandidates(true);
      const [raw, enriched] = await Promise.all([
        getCandidates(jobId),
        getAllCandidates(null, '', 1000, 0, jobId),
      ]);
      setCandidates(raw);
      setEnrichedCandidates(enriched.candidates || []);
      // Auto-switch to public-test tab if no candidates
      if (raw.length === 0 && activeTab === 'candidates') {
        setActiveTab('public-test');
      }
    }
    catch (e) { console.error('Failed to load candidates:', e); } finally { setLoadingCandidates(false); }
  };
  const fetchTopCandidates = async () => {
    try { setLoadingTopCandidates(true); const d = await getTopCandidates(jobId, 10); setTopCandidates(d.top_candidates || []); }
    catch (e) { console.error('Failed to load top candidates:', e); } finally { setLoadingTopCandidates(false); }
  };
  const fetchHeatmap = async () => {
    try { setLoadingHeatmap(true); const d = await getLocationHeatmap(jobId); setHeatmapData(d.heatmap || []); }
    catch (e) { console.error('Failed to load heatmap:', e); } finally { setLoadingHeatmap(false); }
  };
  const fetchClusters = async () => {
    try { setLoadingClusters(true); const d = await getCandidateClusters(jobId); setClusters(d.clusters || []); }
    catch (e) { console.error('Failed to load clusters:', e); } finally { setLoadingClusters(false); }
  };
  const fetchInterviewQuestions = async () => {
    try { const d = await getInterviewQuestions(jobId); setInterviewQuestions(d.questions || []); }
    catch (e) { console.error('Failed to load interview questions:', e); }
  };

  const handleGenerateQuestions = async () => {
    try {
      setGeneratingQuestions(true);
      const d = await generateInterviewQuestions(jobId);
      setInterviewQuestions(d.questions || []);
      toast.success('Interview questions generated!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate questions'); }
    finally { setGeneratingQuestions(false); }
  };

  const handleUpdateQuestions = async (questions) => {
    try {
      setSavingQuestions(true);
      const d = await updateInterviewQuestions(jobId, questions);
      setInterviewQuestions(d.questions || []);
      toast.success('Questions saved!');
    } catch (e) { toast.error('Failed to save questions'); }
    finally { setSavingQuestions(false); }
  };

  const handleToggleShowResults = async () => {
    const newVal = !(currentJob?.show_results_to_candidate);
    try {
      const updated = await updateJob(jobId, { show_results_to_candidate: newVal });
      setCurrentJob(prev => ({ ...prev, show_results_to_candidate: updated.show_results_to_candidate ?? newVal }));
      toast.success(newVal ? 'Candidates will now see their results' : 'Results hidden from candidates');
    } catch (e) {
      toast.error('Failed to update setting');
    }
  };

  const handleOpenInterviewLink = (candidate) => {
    if (interviewQuestions.length === 0) {
      toast.error('Please generate interview questions first. Go to the AI Interview tab.');
      return;
    }
    setInterviewLinkCandidate(candidate);
    setShowInterviewLinkModal(true);
  };

  const handleUploadResumes = async (files) => {
    try {
      setUploadingResumes(true); setUploadProgress(0);
      const result = await uploadResumes(jobId, files, setUploadProgress);
      toast.success(`Uploaded ${result.processed} resumes`);
      if (result.duplicates > 0) toast.info(`${result.duplicates} duplicates skipped`);
      if (result.failed > 0) toast.warning(`${result.failed} files failed`);
      fetchJobDetails();
    } catch (e) { toast.error('Failed to upload resumes'); }
    finally { setUploadingResumes(false); setUploadProgress(0); }
  };

  const handleAnalyzeResumes = async () => {
    try {
      setAnalyzingResumes(true);
      toast.info('Starting AI analysis... This may take a few minutes.');
      const result = await analyzeResumes(jobId);
      toast.success(`Analyzed ${result.analyzed} candidates!`);
      fetchJobDetails(); fetchCandidates(); fetchTopCandidates(); fetchHeatmap(); fetchClusters();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to analyze resumes'); }
    finally { setAnalyzingResumes(false); }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await exportCandidates(jobId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${currentJob?.title || 'candidates'}_ranked.xlsx`;
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); document.body.removeChild(a);
      toast.success('Export downloaded!');
    } catch (e) { toast.error('Failed to export'); }
    finally { setExporting(false); }
  };

  const handleExportPdf = async () => {
    try {
      setExportingPdf(true);
      const blob = await exportPdfReport(jobId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${currentJob?.title || 'report'}_report.pdf`;
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); document.body.removeChild(a);
      toast.success('PDF report downloaded!');
    } catch (e) { toast.error('Failed to export PDF'); }
    finally { setExportingPdf(false); }
  };

  const toggleCandidateForCompare = (candidateId) => {
    setSelectedForCompare(prev => {
      if (prev.includes(candidateId)) return prev.filter(id => id !== candidateId);
      if (prev.length >= 5) { toast.warning('Max 5 candidates'); return prev; }
      return [...prev, candidateId];
    });
  };

  const handleViewResume = async (candidate) => {
    if (candidate.raw_text) { setSelectedResumeCandidate(candidate); setShowResumeModal(true); return; }
    try {
      const full = await getCandidateDetail(jobId, candidate.id);
      setSelectedResumeCandidate(full); setShowResumeModal(true);
    } catch (e) { toast.error('Failed to load resume'); }
  };

  const handleCompare = async () => {
    if (selectedForCompare.length < 2) { toast.error('Select at least 2 candidates'); return; }
    try {
      setLoadingCompare(true);
      const d = await compareCandidates(jobId, selectedForCompare);
      setComparisonData(d); setShowCompareModal(true);
    } catch (e) { toast.error('Failed to compare'); }
    finally { setLoadingCompare(false); }
  };

  const refreshAll = () => {
    fetchCandidates(); fetchTopCandidates(); fetchHeatmap(); fetchClusters();
  };

  if (loadingJob) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-4 gap-6">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}</div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!currentJob) return null;

  const analyzedCount = candidates.filter(c => c.analysis).length;
  const hasUnanalyzed = currentJob.total_resumes > analyzedCount;

  return (
    <div className="space-y-6" data-testid="job-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <Button variant="ghost" size="sm" className="mb-2 -ml-2" onClick={() => navigate('/jobs')} data-testid="back-to-jobs-btn">
            <ChevronLeft className="h-4 w-4 mr-1" /> All Jobs
          </Button>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {currentJob.title}
          </h1>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Badge variant="outline">{currentJob.job_type}</Badge>
            {currentJob.client_name && (
              <Badge variant="secondary" className="gap-1">
                <Building2 className="h-3 w-3" /> {currentJob.client_name}
              </Badge>
            )}
            {currentJob.city && <Badge variant="secondary">{currentJob.city}, {currentJob.country}</Badge>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowPublicLinkModal(true)}
            className="gap-1.5"
            data-testid="public-link-btn"
          >
            <Link2 className="h-4 w-4" />
            Public Link
          </Button>
          {candidates.length > 0 && (
            <>
              <Button variant="outline" onClick={handleExportPdf} disabled={exportingPdf} data-testid="export-pdf-btn">
                {exportingPdf ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileDown className="h-4 w-4 mr-2" />}
                PDF
              </Button>
              <Button variant="outline" onClick={handleExport} disabled={exporting} data-testid="export-btn">
                {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
                Excel
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-5"><div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-indigo-500/10"><FileText className="h-5 w-5 text-indigo-500" strokeWidth={1.5} /></div>
          <div><p className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{currentJob.total_resumes || 0}</p><p className="text-xs text-muted-foreground">Resumes</p></div>
        </div></CardContent></Card>
        <Card><CardContent className="p-5"><div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-emerald-500/10"><Brain className="h-5 w-5 text-emerald-500" strokeWidth={1.5} /></div>
          <div><p className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{analyzedCount}</p><p className="text-xs text-muted-foreground">Analyzed</p></div>
        </div></CardContent></Card>
        <Card><CardContent className="p-5"><div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-blue-500/10"><Users className="h-5 w-5 text-blue-500" strokeWidth={1.5} /></div>
          <div><p className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{candidates.filter(c => c.fit_label === 'Strong Fit').length}</p><p className="text-xs text-muted-foreground">Strong Fits</p></div>
        </div></CardContent></Card>
        <Card><CardContent className="p-5"><div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-amber-500/10"><BarChart3 className="h-5 w-5 text-amber-500" strokeWidth={1.5} /></div>
          <div><p className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{currentJob.top_score ? Math.round(currentJob.top_score) : '-'}</p><p className="text-xs text-muted-foreground">Top Score</p></div>
        </div></CardContent></Card>
      </div>

      {/* JD Analysis + MI Analysis + Discovery Questions + Smart Sourcing + AI Interview — Tabbed */}
      <Tabs defaultValue="jd-analysis" className="w-full">
        <div className="flex items-center justify-between">
          <TabsList className="grid grid-cols-5 w-auto">
            <TabsTrigger value="jd-analysis" className="gap-2 px-3" data-testid="tab-jd-analysis">
              <Target className="h-4 w-4" /> JD Intelligence
            </TabsTrigger>
            <TabsTrigger value="mi-analysis" className="gap-2 px-3" data-testid="tab-mi-analysis">
              <Globe className="h-4 w-4" /> MI Analysis
            </TabsTrigger>
            <TabsTrigger value="discovery" className="gap-2 px-3" data-testid="tab-discovery">
              <Brain className="h-4 w-4" /> Discovery
            </TabsTrigger>
            <TabsTrigger value="sourcing" className="gap-2 px-3" data-testid="tab-sourcing">
              <Sparkles className="h-4 w-4" /> Smart Sourcing
            </TabsTrigger>
            <TabsTrigger value="interview" className="gap-2 px-3" data-testid="tab-interview">
              <Video className="h-4 w-4" /> AI Interview
            </TabsTrigger>
          </TabsList>
          {!readOnly && <ShareDropdown jobId={jobId} hasJDData={!!currentJob.jd_analysis} onOpenCombinedShare={() => setShowCombinedShareModal(true)} />}
        </div>
        <TabsContent value="jd-analysis" className="mt-4">
          <JDAnalysisPanel analysis={currentJob.jd_analysis} isLoading={currentJob.jd_analysis_status === 'queued' && !currentJob.jd_analysis}
            pdfUrl={`${process.env.REACT_APP_BACKEND_URL}/api/jobs/${jobId}/jd-pdf`} />
        </TabsContent>
        <TabsContent value="mi-analysis" className="mt-4">
          <MarketIntelligenceTab
            jobId={jobId}
            jobTitle={currentJob?.title || ''}
            jobDescription={currentJob?.job_description || ''}
            jobLocation={[currentJob?.city, currentJob?.state, currentJob?.country].filter(Boolean).join(', ') || 'United States'}
            jobType={currentJob?.job_type || 'onsite'}
            companyName={currentJob?.client_name || ''}
            readOnly={readOnly}
          />
        </TabsContent>
        <TabsContent value="discovery" className="mt-4">
          <DiscoveryQuestionsTab
            jobId={jobId}
            jobTitle={currentJob?.title || ''}
            jobDescription={currentJob?.job_description || ''}
            companyName={currentJob?.client_name || ''}
            readOnly={readOnly}
          />
        </TabsContent>
        <TabsContent value="sourcing" className="mt-4">
          <SourcingStrategyTab
            jobId={jobId}
            jobTitle={currentJob?.title || ''}
            jobDescription={currentJob?.job_description || ''}
            jobLocation={currentJob?.city ? `${currentJob.city}, ${currentJob.country || ''}` : (currentJob?.country || 'United States')}
            jobType={currentJob?.job_type || 'onsite'}
            companyName={currentJob?.client_name || ''}
          />
        </TabsContent>
        <TabsContent value="interview" className="mt-4">
          <div className="space-y-6">
            <InterviewQuestionsEditor
              jobId={jobId} questions={interviewQuestions} onQuestionsUpdate={handleUpdateQuestions}
              onGenerate={handleGenerateQuestions} isGenerating={generatingQuestions} isSaving={savingQuestions}
              showResultsToCandidate={currentJob?.show_results_to_candidate || false}
              onToggleShowResults={handleToggleShowResults}
            />
            {interviewQuestions.length > 0 && candidates.length > 0 && (
              <Card data-testid="interview-candidates-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <Video className="h-5 w-5 text-indigo-500" strokeWidth={1.5} /> Send Interview Links
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">Generate and send video interview links to candidates</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                    {candidates.slice(0, 50).map((candidate) => {
                      const parsed = candidate.parsed_data || {};
                      return (
                        <div key={candidate.id} className="flex items-center justify-between p-3 border rounded-lg hover:border-indigo-500/30 transition-colors group">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-8 h-8 rounded-full bg-indigo-500/10 flex items-center justify-center shrink-0">
                              <span className="text-xs font-bold text-indigo-500">{(parsed.name || candidate.file_name || '?')[0].toUpperCase()}</span>
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{parsed.name || candidate.file_name}</p>
                              <div className="flex items-center gap-2">
                                {candidate.final_score && <Badge variant="outline" className="text-xs">Score: {Math.round(candidate.final_score)}</Badge>}
                                {candidate.fit_label && <Badge variant="secondary" className="text-xs">{candidate.fit_label}</Badge>}
                              </div>
                            </div>
                          </div>
                          <Button variant="outline" size="sm" onClick={() => handleOpenInterviewLink(candidate)} data-testid={`interview-link-btn-${candidate.id}`}>
                            <Video className="h-3.5 w-3.5 mr-1" /> Interview
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
            <Card data-testid="interview-history-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Clock className="h-5 w-5 text-indigo-500" strokeWidth={1.5} /> Interview History
                </CardTitle>
                <p className="text-sm text-muted-foreground">All interviews for this job</p>
              </CardHeader>
              <CardContent><InterviewHistory /></CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Resume Upload + ATS Import */}
      {!readOnly && <div className="flex items-center gap-3">
        <div className="flex-1">
          <ResumeUploadZone onUpload={handleUploadResumes} isUploading={uploadingResumes} uploadProgress={uploadProgress} />
        </div>
        <Button variant="outline" className="gap-2 h-20 px-4 shrink-0" onClick={() => setShowATSCandidateImport(true)} data-testid="import-candidate-ats-btn">
          <Download className="h-4 w-4" />
          <div className="text-left">
            <p className="text-xs font-medium">Import from ATS</p>
            <p className="text-[10px] text-muted-foreground">Search Ceipal</p>
          </div>
        </Button>
      </div>}

      {/* Batch Analysis Progress */}
      {currentJob.total_resumes > 0 && (
        <BatchProgressTracker
          jobId={jobId}
          totalResumes={currentJob.total_resumes}
          analyzedCount={analyzedCount}
          readOnly={readOnly}
          onComplete={() => { fetchJobDetails(); fetchCandidates(); fetchTopCandidates(); fetchHeatmap(); fetchClusters(); }}
        />
      )}

      {/* Tabs — candidate-related tabs + Public Test */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className={`grid w-full mb-4 ${candidates.length > 0 ? 'grid-cols-5' : 'grid-cols-1'}`}>
          {candidates.length > 0 && (
            <>
              <TabsTrigger value="candidates" className="gap-2" data-testid="tab-candidates">
                <Users className="h-4 w-4" /><span className="hidden sm:inline">All Candidates</span><span className="sm:hidden">All</span>
              </TabsTrigger>
              <TabsTrigger value="top10" className="gap-2" data-testid="tab-top10">
                <Trophy className="h-4 w-4" /><span className="hidden sm:inline">Top 10</span><span className="sm:hidden">Top</span>
              </TabsTrigger>
              <TabsTrigger value="clusters" className="gap-2" data-testid="tab-clusters">
                <Layers className="h-4 w-4" /><span className="hidden sm:inline">Clusters</span><span className="sm:hidden">Groups</span>
              </TabsTrigger>
              <TabsTrigger value="heatmap" className="gap-2" data-testid="tab-heatmap">
                <MapPin className="h-4 w-4" /><span className="hidden sm:inline">Heatmap</span><span className="sm:hidden">Map</span>
              </TabsTrigger>
            </>
          )}
          <TabsTrigger value="public-test" className="gap-2" data-testid="tab-public-test">
            <Globe className="h-4 w-4" /><span className="hidden sm:inline">Public Test</span><span className="sm:hidden">Public</span>
          </TabsTrigger>
        </TabsList>

        {candidates.length > 0 && (
          <>
            <TabsContent value="candidates" className="mt-0">
              <SmartCandidateTable
                candidates={enrichedCandidates}
                jobs={[currentJob].filter(Boolean)}
                loading={loadingCandidates}
                showJobColumn={false}
                showBulkActions={true}
                jobId={jobId}
                onViewResume={(c) => handleViewResume({ id: c.id, parsed_data: { name: c.name }, file_name: c.file_name })}
                onOpenInterview={(c) => handleOpenInterviewLink({ id: c.id, parsed_data: { name: c.name, email: c.email } })}
                onBulkLinksGenerated={fetchCandidates}
                pageSize={25}
              />
            </TabsContent>

            <TabsContent value="top10" className="mt-0">
              <TopCandidates topCandidates={topCandidates} isLoading={loadingTopCandidates} onViewCandidate={() => setActiveTab('candidates')} />
            </TabsContent>

            <TabsContent value="clusters" className="mt-0">
              <CandidateClusters clusters={clusters} isLoading={loadingClusters} />
            </TabsContent>

            <TabsContent value="heatmap" className="mt-0">
              <TalentHeatmap heatmapData={heatmapData} isLoading={loadingHeatmap} />
            </TabsContent>
          </>
        )}

          <TabsContent value="public-test" className="mt-0">
            <PublicTestAnalytics jobId={jobId} />
          </TabsContent>
        </Tabs>

      {/* Modals */}
      <QuickCompareModal isOpen={showCompareModal} onClose={() => { setShowCompareModal(false); setComparisonData(null); }}
        comparisonData={comparisonData} isLoading={loadingCompare} onViewResume={handleViewResume} />
      <ResumeViewerModal isOpen={showResumeModal} onClose={() => { setShowResumeModal(false); setSelectedResumeCandidate(null); }}
        candidate={selectedResumeCandidate} />
      <InterviewLinkModal isOpen={showInterviewLinkModal} onClose={() => { setShowInterviewLinkModal(false); setInterviewLinkCandidate(null); }}
        candidate={interviewLinkCandidate} jobId={jobId} jobTitle={currentJob?.title} hasQuestions={interviewQuestions.length > 0} />
      <PublicLinkModal
        isOpen={showPublicLinkModal}
        onClose={() => setShowPublicLinkModal(false)}
        jobId={jobId}
        jobTitle={currentJob?.title}
        hasQuestions={interviewQuestions.length > 0}
      />
      <CombinedShareModal
        isOpen={showCombinedShareModal}
        onClose={() => setShowCombinedShareModal(false)}
        jobId={jobId}
        jobTitle={currentJob?.title}
        onJDGenerated={fetchJobDetails}
      />
      <ATSCandidateImportModal
        isOpen={showATSCandidateImport}
        onClose={() => setShowATSCandidateImport(false)}
        region={currentJob?.region || 'APAC'}
        jobId={jobId}
        onImported={fetchCandidates}
      />
    </div>
  );
}
